import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Row, Col, Space, Alert, Image, message, Select } from 'antd';
import SignatureCanvas from 'react-signature-canvas';
import { useGlobalModel } from '@/model/globalModel';
import { getBaseImgLookUrl, getBaseImgUploadUrl } from '@/utils/constants';
import { httpRequest } from "../../../../utils/HttpRequest";
import { useReportBillModel } from '../reportBillModel';
import { EditOutlined } from '@ant-design/icons';
import { reportBillService } from '../reportBillService';

export const ApprovalModal: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signature, setSignature] = useState('');
  const sigCanvas = useRef<SignatureCanvas>(null);
  const { checkAcceptVisible, setCheckAcceptVisible, selectedRowKeys, loadData } = useReportBillModel();
  const { shopAndOrg: { shopId }, user: { roles, token } } = useGlobalModel();
  const master = roles.includes('hd-j') || false;
  const [formval, setFormval] = useState<any>({});
  const [readSignature, setReadSignature] = useState<any>(false);
  const [loading, setLoading] = useState(false); // 添加loading状态
  const [detailData, setDetailData] = useState<any>({}); // 添加详情数据状态
  const { shopAndOrg: { shopType }, allShopDs } = useGlobalModel();

  // 初始化表单数据和获取详情
  useEffect(() => {
    if (checkAcceptVisible) {
      const id = selectedRowKeys[0];
      // setFormval({ code: id });
      setSignature('');
      sigCanvas.current?.clear();
      setReadSignature(false);
      fetchDetail(id); // 调用获取详情的函数
    }
  }, [checkAcceptVisible]);

  // 获取详情数据
  const fetchDetail = async (id: string) => {
    setLoading(true);
    try {
      const res = await reportBillService.one(id);
      setDetailData({ ...res.retData });

      // 将查询到的信息合并到formval中
      setFormval({
        ...res.retData,
        code: res.retData.code,
        repairType: res.retData.repairType ?? 0,
      });
    } catch (error) {
      message.error('获取详情失败');
      console.error('获取详情失败', error);
    } finally {
      setLoading(false);
    }
  };

  // 检查是否可以提交审批
  const canSubmit = () => {
    // 审核驳回需要填写意见
    return formval.approvalOpinion;
  };

  // 关闭弹窗
  const handleClose = () => {
    setFormval({});
    setSignature('');
    sigCanvas.current?.clear();
    setReadSignature(false);
    setCheckAcceptVisible(false);
  };

  // 数据URL转Blob
  const dataURLToBlob = (dataURL: string) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime || 'image/png' });
  };

  // 上传签名图片
  const uploadSignature = async (dataURL: string) => {
    if (!dataURL) {
      throw new Error('未签名！');
    }
    try {
      const blob = dataURLToBlob(dataURL);
      const formData = new FormData();
      formData.append('appCode', 'hd-vrm');
      formData.append('fileMode', '0');
      formData.append('file', blob, 'signature.png');
      const response = await fetch(`${getBaseImgUploadUrl()}`, {
        method: 'POST',
        body: formData,
        headers: { 'Authorization': token }
      });

      if (!response.ok) {
        throw new Error(`上传失败: ${response.status}`);
      }

      const result = await response.json();

      if (result && result.retData) {
        setSignature(result.retData);
        return result.retData;
      } else {
        throw new Error('服务器返回数据格式不正确');
      }
    } catch (error: any) {
      console.error('上传签名失败', error);
      throw new Error(error.message || '签名上传失败，请重试');
    }
  };

  // 处理签名变化
  const onSignatureChange = async () => {
    if (!readSignature) {
      message.error('请先签名');
      return false;
    }

    try {
      const dataURL = sigCanvas.current?.toDataURL() || '';

      if (dataURL) {
        const isUploaded = await uploadSignature(dataURL);
        return isUploaded;
      }

      return;
    } catch (error: any) {
      message.error(error.message);
      console.error('处理签名错误', error);
      return false;
    }
  };

  // 清除签名
  const clearSignature = () => {
    setSignature('');
    sigCanvas.current?.clear();
    setReadSignature(false);
  };

  const onSignatureBegin = () => {
    setReadSignature(true);
  };

  // 审核通过处理
  const handlePass = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // 先上传签名并等待结果
      const isSignatureUploaded = await onSignatureChange();

      // 检查签名是否上传成功
      if (!isSignatureUploaded) {
        setIsSubmitting(false);
        return;
      }
      // 构建提交数据
      const submitData = {
        code: formval.code,
        roleCodes: roles || [],
        status: 2, // 审核通过状态码
        signatureMsg: isSignatureUploaded,
        approvalOpinion: formval.approvalOpinion,
        repairType: formval.repairType,
        shopId: formval.shopId,
        shopName: formval.shopName,
      };

      // 提交审批
      const result = await reportBillService.submitApprovalStatus(submitData);
      setIsSubmitting(false);
      console.log(result);

      if (result.success) {
        handleClose();
        message.success('审核通过成功');
        loadData()
      } else {
        // throw new Error(result || '审核通过失败，请稍后再试');
      }
    } catch (error: any) {
      message.error(error.message);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 审核驳回处理
  const handleReject = async () => {
    if (isSubmitting) return;

    try {
      if (!canSubmit()) {
        message.warning('请填写驳回意见');
        return;
      }

      setIsSubmitting(true);

      // 先上传签名并等待结果
      const isSignatureUploaded = await onSignatureChange();

      // 检查签名是否上传成功
      if (!isSignatureUploaded) {
        setIsSubmitting(false);
        return;
      }
      // 构建提交数据
      const submitData = {
        code: formval.code,
        roleCodes: roles || [],
        status: 8, // 审核驳回状态码
        signatureMsg: isSignatureUploaded,
        approvalOpinion: formval.approvalOpinion,
        repairType: formval.repairType,
        shopId: formval.shopId,
        shopName: formval.shopName,
      };

      // 提交审批
      const result = await reportBillService.submitApprovalStatus(submitData);
      setIsSubmitting(false);
      console.log(result);

      if (result.success) {
        handleClose();
        message.success('审核驳回成功');
        loadData()
      } else {
        // throw new Error(result || '审核驳回失败，请稍后再试');
      }
    } catch (error: any) {
      message.error(error.message);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title="审批单审核"
      visible={checkAcceptVisible}
      onOk={handleClose}
      onCancel={handleClose}
      footer={null}
      width={600}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* 审批单号 */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#333' }}>审批单号：</div>
          <div style={{ padding: '8px', }}>
            {formval.code}
          </div>
        </div>

        {/* 工单类型 */}
        {/* <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#333' }}>工单类型：</div>
          {master ? (
            <Space>
              <Button
                type={formval.repairType === 0 ? 'primary' : 'default'}
                onClick={() => setFormval({ ...formval, repairType: 0 })}
              >
                内修
              </Button>
              <Button
                type={formval.repairType === 1 ? 'primary' : 'default'}
                onClick={() => setFormval({ ...formval, repairType: 1 })}
              >
                外修
              </Button>
            </Space>
          ) : (
            <div style={{ padding: '8px', border: '1px solid #d9d9d9', borderRadius: 4, backgroundColor: '#fafafa' }}>
              {formval.repairType === 0 ? '内修' : formval.repairType === 1 ? '外修' : '未选定'}
            </div>
          )}
        </div> */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#333' }}>所属站点：</div>
          {/* {master ? (
            <Select
              style={{ width: '80%' }}
              options={allShopDs} //过滤掉直发库
              placeholder="请选择所属站点"
              onChange={(value: string, option: any) => {
                value && setFormval({ ...formval, shopName: option.label, shopId: value })
              }}
            />
          ) : ( */}
          <div >
            {formval.shopName}
          </div>
          {/* )} */}
        </div>
        {/* 意见 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontWeight: 'bold', color: '#333' }}>意见：</div>
          <textarea
            rows={4}
            value={formval.approvalOpinion || ''}
            onChange={(e) => setFormval({ ...formval, approvalOpinion: e.target.value })}
            placeholder="请输入审批意见"
            style={{ padding: '8px', border: '1px solid #d9d9d9', borderRadius: 4, resize: 'none' }}
          />
        </div>

        {/* 签名区域 */}
        <div style={{ display: 'flex', flexDirection: 'column', }}>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 'bold', color: '#333' }}>签名确认：</div>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'flex-end', color: '#666666', marginRight: 4, fontSize: '14px' }} onClick={clearSignature}>
              <EditOutlined />清除签字
            </div>
          </div>

          <div style={{ border: '1px dashed #ddd', marginTop: '5px', padding: 8 }}>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              backgroundColor="white"
              canvasProps={{ width: 530, height: 240 }}
              onBegin={onSignatureBegin}
            />
          </div>
        </div>
      </div>

      {/* 按钮栏 */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0 0 0', borderTop: '1px solid #f0f0f0' }}>
        {!isSubmitting && (
          <Space size="large">
            <Button
              type="primary"
              danger
              onClick={handleReject}
            >
              审核驳回
            </Button>
            <Button
              type="primary"
              onClick={handlePass}
            >
              审核通过
            </Button>
          </Space>
        )}
        {isSubmitting && (
          <Button loading>提交中...</Button>
        )}
      </div>
    </Modal >
  );
};    